import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCamera, FaComments, FaPaperPlane, FaPhone, FaSpinner, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fraudGateService } from '../../services/fraudGateService';
import { getDocumentUrl } from '../../services/apiService';

function handleChatError(err) {
  const code = err.response?.data?.code;
  const msg = err.response?.data?.message || err.message;
  if (code === 'CHAT_CONTACT_BLOCKED') {
    toast.warn(msg || 'This message was blocked. Sharing contact information is not allowed on Sparelo.');
  } else if (code === 'CHAT_BANNED') {
    toast.error(msg || 'You are temporarily banned from chat');
  } else if (code === 'CHAT_WINDOW_CLOSED') {
    toast.info(msg || 'Chat is not available for this order.');
  } else if (code === 'CHAT_PHOTO_LIMIT') {
    toast.warn(msg || 'Photo limit reached for this order');
  } else {
    toast.error(msg || 'Could not complete action');
  }
}

export default function OrderChatPanel({ orderId, onClose, viewerRole = 'buyer' }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [photosRemaining, setPhotosRemaining] = useState(5);
  const [voice, setVoice] = useState(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    try {
      const [data, status, voiceState] = await Promise.all([
        fraudGateService.listChatMessages(orderId),
        fraudGateService.getChatStatus(orderId).catch(() => null),
        fraudGateService.getVoiceState(orderId).catch(() => null),
      ]);
      setMessages(Array.isArray(data) ? data : []);
      if (status?.photosRemaining != null) {
        setPhotosRemaining(status.photosRemaining);
      }
      if (voiceState) setVoice(voiceState);
    } catch (err) {
      if (err.response?.data?.code !== 'CHAT_WINDOW_CLOSED') {
        toast.error(err.response?.data?.message || err.message || 'Could not load chat');
      }
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const msg = await fraudGateService.sendChatMessage(orderId, text);
      setMessages((prev) => [...prev, msg]);
      setBody('');
    } catch (err) {
      handleChatError(err);
    } finally {
      setSending(false);
    }
  };

  const sendPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || sending) return;
    if (photosRemaining <= 0) {
      toast.warn('Maximum 5 photos per order chat');
      return;
    }
    setSending(true);
    try {
      const msg = await fraudGateService.sendChatPhoto(orderId, file, body.trim());
      setMessages((prev) => [...prev, msg]);
      setBody('');
      setPhotosRemaining((n) => Math.max(0, n - 1));
    } catch (err) {
      handleChatError(err);
    } finally {
      setSending(false);
    }
  };

  const requestVoice = async () => {
    setSending(true);
    try {
      await fraudGateService.requestVoice(orderId);
      toast.success('Voice request sent — waiting for acceptance');
      await load();
    } catch (err) {
      handleChatError(err);
    } finally {
      setSending(false);
    }
  };

  const acceptVoice = async () => {
    const sessionId = voice?.activeSession?.sessionId;
    if (!sessionId) return;
    setSending(true);
    try {
      await fraudGateService.acceptVoice(orderId, sessionId);
      toast.success('Voice call started (not recorded — duration logged only)');
      await load();
    } catch (err) {
      handleChatError(err);
    } finally {
      setSending(false);
    }
  };

  const rejectVoice = async () => {
    const sessionId = voice?.activeSession?.sessionId;
    if (!sessionId) return;
    setSending(true);
    try {
      await fraudGateService.rejectVoice(orderId, sessionId);
      toast.info('Voice request declined');
      await load();
    } catch (err) {
      handleChatError(err);
    } finally {
      setSending(false);
    }
  };

  const endVoice = async () => {
    const sessionId = voice?.activeSession?.sessionId;
    if (!sessionId) return;
    setSending(true);
    try {
      const result = await fraudGateService.endVoice(orderId, sessionId);
      toast.success(`Call ended (${result?.durationSeconds ?? 0}s logged)`);
      await load();
    } catch (err) {
      handleChatError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4">
      <div className="flex h-[min(520px,85vh)] w-full max-w-md flex-col rounded-2xl border border-slate-700 bg-slate-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
          <h3 className="flex items-center gap-2 text-sm font-bold text-white">
            <FaComments className="text-amber-400" />
            Order chat
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
            aria-label="Close chat"
          >
            <FaTimes />
          </button>
        </div>

        <p className="border-b border-slate-800 px-4 py-2 text-xs text-slate-500">
          Masked chat only — contact info and links are blocked. Photos: {photosRemaining}/5 left.
        </p>

        {voice?.canUseVoice && (
          <div className="border-b border-slate-800 px-4 py-2 flex flex-wrap gap-2 items-center">
            <FaPhone className="text-emerald-400 text-xs" />
            {voice.inCall ? (
              <button
                type="button"
                disabled={sending}
                onClick={endVoice}
                className="text-xs font-semibold text-red-300 hover:text-red-200"
              >
                End voice call
              </button>
            ) : voice.pendingForMe ? (
              <>
                <span className="text-xs text-slate-400">Voice requested</span>
                <button
                  type="button"
                  disabled={sending}
                  onClick={acceptVoice}
                  className="text-xs font-semibold text-emerald-400"
                >
                  Accept
                </button>
                <button
                  type="button"
                  disabled={sending}
                  onClick={rejectVoice}
                  className="text-xs text-slate-400"
                >
                  Decline
                </button>
              </>
            ) : voice.pendingFromMe ? (
              <span className="text-xs text-slate-400">Awaiting voice acceptance…</span>
            ) : (
              <button
                type="button"
                disabled={sending}
                onClick={requestVoice}
                className="text-xs font-semibold text-emerald-400 hover:text-emerald-300"
              >
                Request voice call
              </button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <div className="flex justify-center py-8 text-slate-500">
              <FaSpinner className="animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-8">No messages yet. Ask about your order here.</p>
          ) : (
            messages.map((m) => {
              if (m.senderRole === 'system' || m.messageType === 'system_notification') {
                return (
                  <div key={m.messageId || m._id} className="flex justify-center">
                    <p className="max-w-[90%] rounded-lg bg-slate-800/80 border border-slate-700 px-3 py-2 text-xs text-slate-400 italic text-center">
                      {m.body}
                    </p>
                  </div>
                );
              }

              const isOwn =
                viewerRole === 'seller' ? m.senderRole === 'seller' : m.senderRole === 'buyer';
              const label = isOwn ? 'You' : viewerRole === 'seller' ? 'Buyer' : 'Seller';
              const isPhoto = m.messageType === 'photo' || m.photoUrl;
              return (
                <div
                  key={m.messageId || m._id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                      isOwn
                        ? 'bg-amber-600/90 text-white'
                        : 'bg-slate-800 text-slate-200 border border-slate-700'
                    }`}
                  >
                    <p className="text-[10px] font-semibold uppercase opacity-70 mb-0.5">{label}</p>
                    {isPhoto && m.photoUrl && (
                      <a
                        href={getDocumentUrl(m.photoUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block mb-1"
                      >
                        <img
                          src={getDocumentUrl(m.photoUrl)}
                          alt="Chat attachment"
                          className="max-h-40 rounded-lg object-cover"
                        />
                      </a>
                    )}
                    {m.body ? (
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    ) : null}
                    <p className="text-[10px] opacity-60 mt-1">
                      {m.createdAt
                        ? new Date(m.createdAt).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={send} className="border-t border-slate-700 p-3 flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={sendPhoto}
          />
          <button
            type="button"
            disabled={sending || photosRemaining <= 0}
            onClick={() => fileRef.current?.click()}
            className="rounded-xl border border-slate-600 px-3 py-2 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            aria-label="Send photo"
          >
            <FaCamera />
          </button>
          <input
            type="text"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type a message…"
            maxLength={500}
            className="flex-1 rounded-xl border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={sending || !body.trim()}
            className="rounded-xl bg-amber-600 px-4 py-2 text-white hover:bg-amber-500 disabled:opacity-50"
          >
            {sending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
          </button>
        </form>
      </div>
    </div>
  );
}
